import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';

const TYPE_ICONS = {
  player_registered:'👤', agent_added_player:'🤝', player_selfie:'📷',
  pitch_request_received:'⚽', pitch_request_responded:'✅', join_request:'🏃',
  match_rating:'⭐', trial_application:'📋', player_deleted:'🗑️', group_deleted:'🗑️',
};

function timeAgo(d) {
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff/60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m/60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h/24)}d ago`;
}

export default function Notifications({ toast }) {
  const { token } = useApp();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const limit = 30;

  const load = useCallback(async (p = 1, unread = false) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ limit, page: p, ...(unread && { unread: 'true' }) });
      const res = await fetch(`/api/app/notifications?${qs}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await res.json();
      setNotifications(d.data || []);
      setTotal(d.total || 0);
    } catch(e) { toast?.('Failed to load notifications'); }
    finally { setLoading(false); }
  }, [token, toast]);

  useEffect(() => { load(1, unreadOnly); setPage(1); }, [unreadOnly]);

  const markAllRead = async () => {
    await fetch('/api/app/notifications/read', {
      method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <div style={{ padding:24, maxWidth:800 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h2 style={{ margin:0, fontSize:22, fontWeight:700 }}>Notifications</h2>
          <p style={{ margin:'4px 0 0', color:'var(--text-dim)', fontSize:13 }}>{total} total</p>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={() => setUnreadOnly(v => !v)} style={{ padding:'6px 16px', borderRadius:20, border:'none', cursor:'pointer', fontSize:12, fontWeight:600,
            background: unreadOnly ? '#a7f432' : 'rgba(255,255,255,0.06)', color: unreadOnly ? '#0f1a10' : 'var(--text-dim)' }}>
            Unread only
          </button>
          {unreadCount > 0 && <button onClick={markAllRead} style={{ padding:'6px 16px', borderRadius:20, border:'1px solid rgba(167,244,50,0.3)', cursor:'pointer', fontSize:12, fontWeight:600, background:'none', color:'#a7f432' }}>
            Mark all read
          </button>}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:60, color:'var(--text-dim)' }}>Loading...</div>
      ) : notifications.length === 0 ? (
        <div style={{ textAlign:'center', padding:80, color:'var(--text-dim)' }}>
          <div style={{ fontSize:48, marginBottom:12 }}>🔔</div>
          <div style={{ fontSize:16, fontWeight:600 }}>No notifications yet</div>
          <div style={{ fontSize:13, marginTop:8 }}>Activity from the platform will appear here</div>
        </div>
      ) : (
        <div style={{ background:'var(--card-bg)', borderRadius:12, border:'1px solid var(--border)', overflow:'hidden' }}>
          {notifications.map((n, i) => (
            <div key={n.id} style={{ display:'flex', gap:14, padding:'14px 18px',
              borderBottom: i < notifications.length-1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
              background: n.isRead ? 'transparent' : 'rgba(167,244,50,0.03)' }}>
              <div style={{ fontSize:22, flexShrink:0, marginTop:2 }}>{TYPE_ICONS[n.type] || '🔔'}</div>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight: n.isRead ? 500 : 700, fontSize:14, color:'var(--text-primary)', marginBottom:3 }}>{n.title}</div>
                {n.message && <div style={{ fontSize:13, color:'var(--text-dim)', marginBottom:4 }}>{n.message}</div>}
                <div style={{ fontSize:11, color:'var(--text-dim)' }}>{timeAgo(n.createdAt)}</div>
              </div>
              {!n.isRead && <div style={{ width:8, height:8, borderRadius:'50%', background:'#a7f432', flexShrink:0, marginTop:8 }} />}
            </div>
          ))}
        </div>
      )}

      {total > limit && (
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:16 }}>
          <span style={{ color:'var(--text-dim)', fontSize:13 }}>Showing {(page-1)*limit+1}–{Math.min(page*limit,total)} of {total}</span>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={() => { setPage(p => p-1); load(page-1, unreadOnly); }} disabled={page<=1}
              style={{ background:'var(--card-bg)', border:'1px solid var(--border)', color:'var(--text-primary)', borderRadius:8, padding:'6px 14px', cursor:'pointer', fontSize:13 }}>← Prev</button>
            <button onClick={() => { setPage(p => p+1); load(page+1, unreadOnly); }} disabled={page*limit>=total}
              style={{ background:'var(--card-bg)', border:'1px solid var(--border)', color:'var(--text-primary)', borderRadius:8, padding:'6px 14px', cursor:'pointer', fontSize:13 }}>Next →</button>
          </div>
        </div>
      )}
    </div>
  );
}
